import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, ArrowUpRight, Search, Loader, X, Home } from 'lucide-react';
import { useProperties } from '../hooks/useProperties';
import { useDebounce } from '../hooks/useDebounce';
import LazyImage from '../components/LazyImage';
import Pagination from '../components/Pagination';

const Properties = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput, searchTerm] = useDebounce('', 500);
  const [typeInput, setTypeInput, typeFilter] = useDebounce('', 500);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setTypeInput('');
    setCurrentPage(1);
  };
  
  const { data: queryData, isLoading, error } = useProperties(currentPage, {
    search: searchTerm,
    type: typeFilter,
  });

  const properties = queryData?.properties || [];
  const pagination = queryData?.pagination || {
    currentPage: 1,
    firstPageUrl: '',
    from: 0,
    lastPage: 1,
    lastPageUrl: '',
    links: [],
    nextPageUrl: null,
    path: '',
    perPage: 10,
    prevPageUrl: null,
    to: 0,
    total: 0,
  };

  return (
    <div className="pt-32 pb-24 px-6 bg-white min-h-screen">
      <Helmet>
        <title>Properties to Book | Vacation &amp; Short-Term Rentals | ProjectProse</title>
        <meta
          name="description"
          content="Browse ProjectProse's portfolio of fully furnished vacation rentals and serviced accommodation across the UK. Filter by property type, compare bedrooms and bathrooms, and book with transparent pricing."
        />
        <link rel="canonical" href="https://www.project-prose.com/properties" />
      </Helmet>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="max-w-xl">
            <h2 className="text-sm uppercase tracking-[0.3em] text-prose-brand font-bold mb-4 border-b-4 w-fit border-prose-gold pb-2">Available Properties</h2>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display text-prose-black">
              Properties to <span className="italic font-light text-gray-400\">Book.</span>
            </h1>
          </div>
          <p className="text-gray-500 text-xs sm:text-sm max-w-xs leading-relaxed">
            Discover our featured properties ready to book.
          </p>
        </div>

        <div className="mb-12 space-y-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search properties..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full px-4 sm:px-6 py-2 sm:py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-prose-brand focus:ring-2 focus:ring-prose-brand/10 transition-all placeholder-gray-400 text-sm sm:text-base"
            />
            <Search size={20} className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Property type..."
              value={typeInput}
              onChange={(e) => setTypeInput(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-prose-brand focus:ring-2 focus:ring-prose-brand/10 transition-all placeholder-gray-400 text-xs sm:text-sm"
            />
          </div>

          {(searchInput || typeInput) && (
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-semibold text-prose-brand border border-prose-brand rounded-lg hover:bg-prose-brand/5 hover:scale-105 transition-all duration-300"
            >
              <X size={16} />
              Clear All Filters
            </button>
          )}
        </div>

        {isLoading && (
          <div className="flex justify-center items-center py-20">
            <Loader size={40} className="text-prose-brand animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-center py-20">
            <p className="text-red-500 text-base sm:text-lg">Failed to load properties. Please try again later.</p>
          </div>
        )}

        {!isLoading && properties.length > 0 && (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
              {properties.map((item, index) => (
                <Link key={item.id} to={`/property/${item.id}`} className="bg-white group cursor-pointer overflow-hidden border border-gray-200 rounded-lg hover:shadow hover:scale-105 transition-all duration-300 transform">
                  <div className="relative h-72 overflow-hidden">
                    <LazyImage
                      src={item.images?.[0]?.url || 'https://via.placeholder.com/800x600.png?text=No+Image'} 
                      alt={item.title}
                      className="w-full h-full group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-prose-brand/90 text-white text-[10px] uppercase tracking-widest px-3 py-1 backdrop-blur-sm">
                        {item.status}
                      </span>
                    </div>
                  </div>

                  <div className="p-8 space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-prose-black group-hover:text-prose-brand transition-colors">
                          {item.title}
                        </h3>
                        <p className="flex items-center gap-1 text-gray-400 text-xs sm:text-sm mt-1">
                          <MapPin size={14} /> {item.address.split('\n')[0]}
                        </p>
                      </div>
                      <div className="p-2 border border-gray-100 rounded-full group-hover:bg-prose-gold group-hover:border-prose-gold group-hover:text-prose-black group-hover:rotate-45 transition-all duration-300">
                        <ArrowUpRight size={20} />
                      </div>
                    </div>

                    <div className="pt-6 border-t border-gray-50 grid grid-cols-3 gap-4">
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Type</span>
                        <span className="text-xs sm:text-lg font-bold text-prose-brand capitalize">{item.type}</span>
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Bedrooms</span>
                        <span className="text-xs sm:text-lg font-bold text-prose-brand">{item.bedrooms}</span>
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Bathrooms</span>
                        <span className="text-xs sm:text-lg font-bold text-prose-brand">{item.bathrooms}</span>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-50">
                      <p className="text-lg sm:text-2xl font-display font-bold text-prose-gold">£{parseInt(item.price).toLocaleString()}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <Pagination 
              pagination={pagination}
              currentPage={pagination.currentPage} 
              onPageChange={handlePageChange}
            />
          </>
        )}

        {!isLoading && properties.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 px-6">
            <div className="mb-6 p-4 bg-gray-100 rounded-full">
              <Home size={48} className="text-gray-400" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-prose-black mb-2">No Properties Found</h3>
            <p className="text-gray-500 text-sm sm:text-base mb-8 max-w-md text-center">
              {searchInput || typeInput 
                ? 'Try adjusting your filters or search terms to find what you\'re looking for.'
                : 'We don\'t have any properties available at the moment. Check back soon!'}
            </p>
            {(searchInput || typeInput) && (
              <button
                onClick={handleClearFilters}
                className="inline-flex items-center gap-2 px-6 py-3 bg-prose-brand text-white rounded-lg font-semibold hover:bg-prose-brand/90 hover:scale-105 transition-all duration-300"
              >
                <X size={16} />
                Clear All Filters
              </button>
            )}
          </div>
        )}

        <div className="mt-20 p-12 bg-gradient-to-r from-prose-black via-prose-brand to-prose-black text-white flex flex-col md:flex-row items-center justify-between gap-8 rounded-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none"><div className="absolute top-0 right-0 w-96 h-96 bg-prose-gold rounded-full filter blur-3xl"></div></div>
          <div className="relative z-10">
            <h3 className="text-lg sm:text-2xl md:text-3xl font-display font-bold mb-2">Want your property featured here?</h3>
            <p className="text-gray-200 text-xs sm:text-sm md:text-base">Join our guaranteed rent scheme and start your journey to worry-free ownership.</p>
          </div>
          <Link to="/contact" className="relative z-10 whitespace-nowrap bg-prose-gold text-prose-black px-8 py-4 rounded-lg text-xs sm:text-sm font-bold tracking-widest hover:bg-white hover:text-prose-brand hover:scale-105 transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] inline-block">
            Enquire Now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Properties;